"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import IntentModal from "@components/molecules/IntentModal";
import RequestCallBackModal from "@components/molecules/RequestCallBackModal";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { useAppSelector } from "@lib/redux/hooks";
import type { PageType } from "@constants/pageTypes";
import type { BuyingIntentData } from "@services/globalIntentService";
import { postRequestCallback } from "@services/requestCallbackService";
import {
  buildCallbackMessage,
  getCallbackConfirmationUrl,
} from "@utils/callbackConfirmation";
import {
  trackAppFlowAction,
  trackAppFlowException,
  getLeadActionName,
} from "@utils/appFlowEvents";
import { inApp } from "@utils/platformDetection.client";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import {
  hasAppTalkToUsCallback,
  saveAppTalkToUsCallback,
} from "@utils/appCallbackSession";

type ContinueRedirect = (redirectUrl: string, intent?: string) => void;

interface IntentRedirectModalParams {
  buyingIntent?: BuyingIntentData | null;
  cityName: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  colorName?: string;
  colorHexCodes?: string[];
  pageType?: PageType;
  loginType?: string;
  continueRedirect?: ContinueRedirect;
  disableClose?: boolean;
  callbackSessionKey?: string;
}

interface OpenIntentOrRedirectParams {
  redirectUrl: string;
  continueRedirect?: ContinueRedirect;
}

const shouldShowIntentModal = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return sessionStorage.getItem("buying_intent") !== "false";
};

export const useIntentRedirectModal = ({
  buyingIntent,
  cityName,
  brandName = "",
  modelName = "",
  variantName = "",
  colorName = "",
  colorHexCodes = [],
  pageType,
  loginType = "Get quote",
  continueRedirect,
  disableClose = false,
  callbackSessionKey,
}: IntentRedirectModalParams) => {
  const router = useRouter();
  const userProfile = useAppSelector((state) => state.profile.data);
  const [isIntentModalOpen, setIsIntentModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isRequestCallBackModalOpen, setIsRequestCallBackModalOpen] =
    useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const pendingRedirectUrl = useRef("");
  const pendingContinueRedirect = useRef<ContinueRedirect | undefined>(undefined);
  const isRequestCallbackSubmitting = useRef(false);
  const isMobileApp = inApp();
  const resolvedCallbackSessionKey =
    callbackSessionKey ||
    `talk_to_us_callback:${[brandName, modelName, variantName]
      .filter(Boolean)
      .join("-")}`;

  const defaultContinueRedirect: ContinueRedirect = (redirectUrl) => {
    router.push(redirectUrl);
  };

  const continueWithRedirect = (intent?: string) => {
    const redirectUrl = pendingRedirectUrl.current;
    const redirectHandler =
      pendingContinueRedirect.current || continueRedirect || defaultContinueRedirect;

    if (!redirectUrl) {
      return;
    }

    redirectHandler(redirectUrl, intent);
  };

  const openIntentOrRedirect = ({
    redirectUrl,
    continueRedirect: overrideContinueRedirect,
  }: OpenIntentOrRedirectParams) => {
    pendingRedirectUrl.current = redirectUrl;
    pendingContinueRedirect.current = overrideContinueRedirect;

    if (!buyingIntent || !shouldShowIntentModal()) {
      const redirectHandler =
        overrideContinueRedirect || continueRedirect || defaultContinueRedirect;
      redirectHandler(redirectUrl);
      return false;
    }
    sessionStorage.setItem("flowType", "New Car");
    setIsIntentModalOpen(true);
    return true;
  };

  const handleGetQuoteFromIntent = async (intent?: string) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: "Intent",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_get_detailed_quote",
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_color: colorName,
        vehicle_variant: variantName,
        customer_name: userProfile?.name || "",
        email: userProfile?.email || "",
        phone: userProfile?.contact_no || userProfile?.phone || "",
        intent,
      },
    });
    setIsIntentModalOpen(false);
    continueWithRedirect(intent);
  };

  const handleRequestCallbackFromIntent = async (intent?: string) => {
    if (isRequestCallbackSubmitting.current) {
      return;
    }

    isRequestCallbackSubmitting.current = true;
    if (isMobileApp) {
      showLoader();
    }
    setIsIntentModalOpen(false);
    const callbackTrackingProps = {
      action_type: "tap_btn",
      action_name: "tap_btn_help",
      city_name: cityName,
      vehicle_make: brandName,
      vehicle_model: modelName,
      vehicle_color: colorName,
      vehicle_variant: variantName,
      customer_name: userProfile?.name || "",
      email: userProfile?.email || "",
      phone: userProfile?.contact_no || userProfile?.phone || "",
      intent,
    };

    let callbackSucceeded = false;

    try {
      void trackAppFlowAction(
        {
          pageType,
          cityName,
          brandName,
          modelName,
          variantName,
          colorName,
          customerName: userProfile?.name,
          email: userProfile?.email,
          phone: userProfile?.contact_no || userProfile?.phone,
        },
        {
          action_type: "tap_btn",
          action_name: "tap_btn_call_scheduled",
          selected: intent || "",
        },
      );
      const callbackResult = await postRequestCallback({
        callback_message: buildCallbackMessage({
          brandName,
          modelName,
          variantName,
          cityName,
          intent,
        }),
      });

      if (callbackResult.success) {
        if (isMobileApp) {
          saveAppTalkToUsCallback(resolvedCallbackSessionKey);
        }
        void trackAppFlowAction(
          {
            pageType,
            cityName,
            brandName,
            modelName,
            variantName,
            colorName,
            customerName: userProfile?.name,
            email: userProfile?.email,
            phone: userProfile?.contact_no || userProfile?.phone,
          },
          {
            action_type: "tap_btn",
            action_name:
              getLeadActionName(callbackResult.data?.status),
            selected: intent || "",
          },
        );
        const redirectUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        callbackSucceeded = true;
        hideLoader();
        router.push(getCallbackConfirmationUrl(redirectUrl));
        return;
      }

      console.error("Request callback failed:", callbackResult.error);
      void trackAppFlowException(
        { pageType, cityName, brandName, modelName, variantName, colorName },
        {
          ...callbackTrackingProps,
          exception_type: "API issue",
          exception_name: "callback_error",
          action_type: "system_error",
          action_name: "callback_error",
        },
      );
    } catch (error) {
      console.error("Callback API call failed:", error);
      void trackAppFlowException(
        { pageType, cityName, brandName, modelName, variantName, colorName },
        {
          ...callbackTrackingProps,
          exception_type: "API issue",
          exception_name: "callback_error",
          action_type: "system_error",
          action_name: "callback_error",
          error: error instanceof Error ? error.message : "Unknown error",
        },
      );
    } finally {
      if (isMobileApp && !callbackSucceeded) {
        hideLoader();
      }
      isRequestCallbackSubmitting.current = false;
    }
  };

  const intentModal = (
    <>
      {isIntentModalOpen && (
        <IntentModal
          isOpen={isIntentModalOpen}
          onClose={() => setIsIntentModalOpen(false)}
          carDetails={{
            name: `${brandName} ${modelName} ${variantName}`.trim(),
            color: {
              name: colorName,
              hex: colorHexCodes,
              type: colorHexCodes.length > 1 ? "dual_tone" : "single_tone",
            },
          }}
          onRequestCallback={handleRequestCallbackFromIntent}
          onGetQuote={handleGetQuoteFromIntent}
          buyingIntent={buyingIntent}
          cityName={cityName}
          mmvDetails={{
            make: brandName,
            model: modelName,
            variant: variantName,
            color: colorName,
          }}
          highPriorityCapture={false}
          pageType={pageType}
          loginType={loginType}
          disableClose={disableClose}
        />
      )}

      {isHelpModalOpen && (
        <IntentModal
          isOpen={isHelpModalOpen}
          onClose={() => setIsHelpModalOpen(false)}
          onRequestCallback={handleRequestCallbackFromIntent}
          buyingIntent={buyingIntent}
          cityName={cityName}
          mmvDetails={{ make: brandName, model: modelName, variant: variantName }}
          pageType={pageType}
          loginType={loginType}
          mode="help"
        />
      )}

      {isRequestCallBackModalOpen && (
        <RequestCallBackModal
          isOpen={isRequestCallBackModalOpen}
          onClose={() => setIsRequestCallBackModalOpen(false)}
        />
      )}
    </>
  );

  return {
    intentModal,
    openIntentOrRedirect,
    openIntentModal: () => setIsIntentModalOpen(true),
    openHelpModal: () => {
      if (isMobileApp && hasAppTalkToUsCallback(resolvedCallbackSessionKey)) {
        const redirectUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        router.push(getCallbackConfirmationUrl(redirectUrl));
        return;
      }
      setIsHelpModalOpen(true);
    },
  };
};
